import * as XLSX from 'xlsx-js-style';
import type { CellStyle, WorkSheet } from 'xlsx-js-style';
import { workbookToXlsxBlob } from '../../utils/workbookToXlsxBlob';
import { applyYellowHeaderRowAndAutofilter, setSheetColumnWidths } from '../kpi-dashboard/kpiDashboardExcelExport';
import type {
  BandwidthActivityAllocationRow,
  BandwidthActivityTypeCountTimePoint,
  BandwidthAllocationMetrics,
  BandwidthCapacityDeductionCell,
  BandwidthGroupBreakdownRow,
  BandwidthHeatmapCountsCustomerExportRow,
  BandwidthTeamResourceAllocationRow,
  BandwidthUserCountTimePoint,
} from './bandwidthDashboardTypes';
import {
  buildCapacityDeductionLookups,
  getCapacityDeductionHoursForGroupUser,
  getEffectiveBandwidthCapacityHours,
  getGroupEffectiveCapacityHours,
} from './bandwidthCapacityDeductionTypes';
import { bandwidthExportDateFileSuffix } from './bandwidthExportFileNaming';
import {
  allocationExportPct,
  computeActivityAllocationFooter,
  computeAllocationFooter,
} from './bandwidthAllocation';
import { bwExportDecimal, bwExportMinutes, bwExportPercent, roundBwDecimal } from './bandwidthFormat';
import { exportHeatmapCellValue, type HeatmapValueUnit } from './bandwidthHeatmapMatrix';
import {
  getBandwidthCapacityHoursForWorkLocation,
  getBandwidthUtilizationPct,
} from './bandwidthUtilization';
import {
  prepareBandwidthCustomerSectionExport,
  prepareBandwidthGroupSectionExport,
  type BandwidthCustomerSectionExportRow,
} from './bandwidthKpiExportFilter';

const SUBTOTAL_BOLD_STYLE: CellStyle = {
  font: { bold: true, color: { rgb: '000000' } },
};

/** 0-based sheet row indices (Excel row numbers minus 1). Skips header row 0. */
function applyBoldToRows(ws: WorkSheet, rowIndices0Based: number[], columnCount: number): void {
  for (const r of rowIndices0Based) {
    if (r < 1) continue;
    for (let c = 0; c < columnCount; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = ws[addr];
      if (cell) {
        cell.s = { ...SUBTOTAL_BOLD_STYLE };
      }
    }
  }
}

type SheetCell = string | number;

function toYmdDisplay(isoOrDate: string): string {
  const s = (isoOrDate || '').trim();
  const d = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const cleaned = s.replace(/[^\d-]/g, '').slice(0, 10);
  return cleaned || 'n/a';
}

function bwXlsxFilename(base: string, dateFrom: string, dateTo: string): string {
  return `${base}_${bandwidthExportDateFileSuffix(dateFrom, dateTo)}.xlsx`;
}

export type BandwidthExcelExport = { blob: Blob; fileName: string };

/** Teams + per-user rows (same figures as the dashboard Teams panel). */
export function exportBandwidthGroupSectionExcel(
  groups: BandwidthGroupBreakdownRow[],
  dateFrom: string,
  dateTo: string,
  capacityDeductionByGroupUser: BandwidthCapacityDeductionCell[] = []
): BandwidthExcelExport {
  const exportGroups = prepareBandwidthGroupSectionExport(groups);
  const deductionLookups = buildCapacityDeductionLookups(capacityDeductionByGroupUser);
  const dateLabel = `${toYmdDisplay(dateFrom)} to ${toYmdDisplay(dateTo)}`;
  const headers: SheetCell[] = [
    'Teams',
    'Date',
    'User',
    'Total activity count',
    'Total time spent (hrs)',
    'Avg hrs on activity',
    'Utilization %',
  ];
  const aoa: SheetCell[][] = [headers];
  /** Sheet row index (0-based) for each group total row and the grand total row. */
  const boldRowIndices: number[] = [];

  let sumCount = 0;
  let sumHrs = 0;
  let sumCapacityHours = 0;
  for (const g of exportGroups) {
    boldRowIndices.push(aoa.length);
    const groupCap = getGroupEffectiveCapacityHours(g, dateFrom, dateTo, deductionLookups);
    const groupCapPerPerson = getBandwidthCapacityHoursForWorkLocation(dateFrom, dateTo, g.workLocation);
    sumCapacityHours += groupCap;
    const gUtil = getBandwidthUtilizationPct(g.timeSpentHours, groupCap);
    aoa.push([
      g.groupName,
      dateLabel,
      '',
      g.totalActivityCount,
      bwExportDecimal(g.timeSpentHours),
      bwExportDecimal(g.avgHrsOnActivity),
      bwExportPercent(gUtil),
    ]);
    sumCount += g.totalActivityCount;
    sumHrs += g.timeSpentHours;
    for (const u of g.users) {
      const userDeductionHours = getCapacityDeductionHoursForGroupUser(
        deductionLookups,
        g.groupId,
        u.userId
      );
      const userCapHours = getEffectiveBandwidthCapacityHours(groupCapPerPerson, userDeductionHours);
      const uUtil = getBandwidthUtilizationPct(u.timeSpentHours, userCapHours);
      aoa.push([
        g.groupName,
        dateLabel,
        u.userName,
        u.totalActivityCount,
        bwExportDecimal(u.timeSpentHours),
        bwExportDecimal(u.avgHrsOnActivity),
        bwExportPercent(uUtil),
      ]);
    }
  }
  const grandAvg = sumCount > 0 ? roundBwDecimal(sumHrs / sumCount) : '';
  const grandUtil = getBandwidthUtilizationPct(sumHrs, sumCapacityHours);
  boldRowIndices.push(aoa.length);
  aoa.push([
    'Total',
    dateLabel,
    '',
    sumCount,
    bwExportDecimal(sumHrs),
    grandAvg,
    bwExportPercent(grandUtil),
  ]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyBoldToRows(ws, boldRowIndices, headers.length);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  setSheetColumnWidths(ws, [28, 18, 24, 18, 22, 18, 14]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Teams');
  const fileName = bwXlsxFilename('bandwidth_dashboard_teams', dateFrom, dateTo);
  return { blob: workbookToXlsxBlob(wb), fileName };
}

/** Daywise team/user effort rows (Teams panel detail drill-down). */
export function exportBandwidthGroupDaywiseDetailExcel(
  groups: BandwidthGroupBreakdownRow[],
  dateFrom: string,
  dateTo: string
): BandwidthExcelExport | null {
  const exportGroups = prepareBandwidthGroupSectionExport(groups);
  const rangeLabel = `${toYmdDisplay(dateFrom)} to ${toYmdDisplay(dateTo)}`;
  const headers: SheetCell[] = [
    'Teams',
    'User',
    'Date',
    'Total activity count',
    'Total time spent (hrs)',
    'Avg hrs on activity',
  ];
  const aoa: SheetCell[][] = [headers];

  for (const g of exportGroups) {
    for (const u of g.users) {
      const days = u.days ?? [];
      if (days.length > 0) {
        for (const d of days) {
          aoa.push([
            g.groupName,
            u.userName,
            d.workedDate,
            d.totalActivityCount,
            bwExportDecimal(d.timeSpentHours),
            bwExportDecimal(d.avgHrsOnActivity),
          ]);
        }
      } else {
        aoa.push([
          g.groupName,
          u.userName,
          rangeLabel,
          u.totalActivityCount,
          bwExportDecimal(u.timeSpentHours),
          u.avgHrsOnActivity != null ? roundBwDecimal(u.avgHrsOnActivity) : 0,
        ]);
      }
    }
  }

  if (aoa.length <= 1) return null;

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  setSheetColumnWidths(ws, [28, 24, 14, 18, 22, 18]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Daywise efforts');
  const fileName = bwXlsxFilename('bandwidth_dashboard_teams_daywise', dateFrom, dateTo);
  return { blob: workbookToXlsxBlob(wb), fileName };
}

/** Customer + per-user rows (same figures as the dashboard Customer panel). */
export function exportBandwidthCustomerSectionExcel(
  rows: BandwidthCustomerSectionExportRow[],
  dateFrom: string,
  dateTo: string
): BandwidthExcelExport {
  const exportRows = prepareBandwidthCustomerSectionExport(rows);
  const headers: SheetCell[] = [
    'Customer',
    'Assigned user',
    'Total activity count',
    'Total time spent (hrs)',
    'Avg hrs on activity',
  ];
  const aoa: SheetCell[][] = [headers];
  const boldRowIndices: number[] = [];

  let sumCount = 0;
  let sumHrs = 0;
  for (const r of exportRows) {
    boldRowIndices.push(aoa.length);
    aoa.push([
      r.customer,
      '',
      r.totalActivity,
      bwExportDecimal(r.timeHrs),
      bwExportDecimal(r.avgHrsOnActivity),
    ]);
    sumCount += r.totalActivity;
    sumHrs += r.timeHrs;
    for (const u of r.users) {
      aoa.push([
        r.customer,
        u.userName,
        u.totalActivity,
        bwExportDecimal(u.timeHrs),
        bwExportDecimal(u.avgHrsOnActivity),
      ]);
    }
  }
  const totalAvgHrs = sumCount > 0 ? roundBwDecimal(sumHrs / sumCount) : '';
  boldRowIndices.push(aoa.length);
  aoa.push(['Total', '', sumCount, bwExportDecimal(sumHrs), totalAvgHrs]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyBoldToRows(ws, boldRowIndices, headers.length);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  setSheetColumnWidths(ws, [28, 24, 18, 22, 18]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Customer');
  const fileName = bwXlsxFilename('bandwidth_dashboard_customer', dateFrom, dateTo);
  return { blob: workbookToXlsxBlob(wb), fileName };
}

export function exportBandwidthHeatmapCountsByCustomerExcel(
  rows: BandwidthHeatmapCountsCustomerExportRow[],
  filePrefix: string,
  dateFrom: string,
  dateTo: string
): BandwidthExcelExport {
  const headers: SheetCell[] = [
    'Customer',
    'Total activity count',
    'Time spent (minutes)',
    'Time spent (hrs)',
    'Avg hrs / activity',
    'Utilization %',
  ];
  const aoa: SheetCell[][] = [headers];
  let sumAct = 0;
  let sumMin = 0;
  let sumHrs = 0;
  for (const r of rows) {
    sumAct += r.totalActivity;
    sumMin += r.timeMinutes;
    sumHrs += r.timeHrs;
  }
  for (const r of rows) {
    aoa.push([
      r.customer,
      r.totalActivity,
      bwExportMinutes(r.timeMinutes),
      bwExportDecimal(r.timeHrs),
      bwExportDecimal(r.avgHrsOnActivity),
      bwExportPercent(r.utilizationPct),
    ]);
  }
  const totalAvg = sumAct > 0 ? roundBwDecimal(sumHrs / sumAct) : '';
  const totalUtil = getBandwidthUtilizationPct(sumHrs, sumHrs);
  const totalRowIdx = aoa.length;
  aoa.push([
    'Total',
    sumAct,
    bwExportMinutes(sumMin),
    bwExportDecimal(sumHrs),
    totalAvg,
    bwExportPercent(totalUtil),
  ]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyBoldToRows(ws, [totalRowIdx], headers.length);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  setSheetColumnWidths(ws, [28, 20, 22, 18, 18, 16]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Counts');
  const fileName = bwXlsxFilename(filePrefix, dateFrom, dateTo);
  return { blob: workbookToXlsxBlob(wb), fileName };
}

export function exportBandwidthHeatmapMatrixExcel(params: {
  filePrefix: string;
  sheetName: string;
  rowLabelHeader: string;
  rowLabels: string[];
  columnLabels: string[];
  cells: number[][];
  dateFrom: string;
  dateTo: string;
  /** When `min`, export whole minutes; when `percent`, export 2-decimal percent values. */
  cellValueUnit?: HeatmapValueUnit;
}): BandwidthExcelExport | null {
  const { filePrefix, sheetName, rowLabelHeader, rowLabels, columnLabels, cells, dateFrom, dateTo, cellValueUnit = 'min' } = params;
  if (rowLabels.length === 0 || columnLabels.length === 0) return null;

  const headers: SheetCell[] = [rowLabelHeader, ...columnLabels];
  const aoa: SheetCell[][] = [headers];
  const colSums = columnLabels.map((_, ci) => {
    const sum = rowLabels.reduce((s, _, ri) => s + (cells[ri]?.[ci] ?? 0), 0);
    return exportHeatmapCellValue(sum, cellValueUnit);
  });

  for (let ri = 0; ri < rowLabels.length; ri++) {
    const row = cells[ri] ?? [];
    aoa.push([
      rowLabels[ri],
      ...columnLabels.map((_, ci) => {
        const v = row[ci] ?? 0;
        return exportHeatmapCellValue(v, cellValueUnit);
      }),
    ]);
  }
  const totalRowIdx = aoa.length;
  aoa.push(['Total', ...colSums]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyBoldToRows(ws, [totalRowIdx], headers.length);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  const wchFirst = 22;
  const restW = Math.max(10, Math.min(18, Math.floor(200 / Math.max(1, columnLabels.length))));
  setSheetColumnWidths(ws, [wchFirst, ...columnLabels.map(() => restW)]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  const fileName = bwXlsxFilename(filePrefix, dateFrom, dateTo);
  return { blob: workbookToXlsxBlob(wb), fileName };
}

export function exportBandwidthLabelCountExcel(
  rows: Array<{ label: string; count: number }>,
  rowHeader: string,
  valueHeader: string,
  filePrefix: string,
  sheetName: string,
  dateFrom: string,
  dateTo: string
): BandwidthExcelExport {
  const headers: SheetCell[] = [rowHeader, valueHeader];
  const aoa: SheetCell[][] = [headers];
  let sum = 0;
  for (const r of rows) {
    aoa.push([r.label, r.count]);
    sum += r.count;
  }
  const totalRowIdx = aoa.length;
  aoa.push(['Total', sum]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyBoldToRows(ws, [totalRowIdx], headers.length);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  setSheetColumnWidths(ws, [40, 16]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  const fileName = bwXlsxFilename(filePrefix, dateFrom, dateTo);
  return { blob: workbookToXlsxBlob(wb), fileName };
}

/** Pass rows in the same order as the on-screen table (e.g. panel `sorted`). */
export function exportBandwidthActivityTypeCountTimeExcel(
  rows: BandwidthActivityTypeCountTimePoint[],
  dateFrom: string,
  dateTo: string
): BandwidthExcelExport {
  const headers: SheetCell[] = ['Activity type', 'Activity count', 'Time (hrs)', 'Avg hrs / activity'];
  const aoa: SheetCell[][] = [headers];
  let sumCount = 0;
  let sumHrs = 0;
  for (const r of rows) {
    const avg = r.totalActivityCount > 0 ? r.timeSpentHours / r.totalActivityCount : null;
    aoa.push([
      r.activityTypeLabel,
      r.totalActivityCount,
      bwExportDecimal(r.timeSpentHours),
      bwExportDecimal(avg),
    ]);
    sumCount += r.totalActivityCount;
    sumHrs += r.timeSpentHours;
  }
  const totalAvg = sumCount > 0 ? roundBwDecimal(sumHrs / sumCount) : '';
  const totalRowIdx = aoa.length;
  aoa.push(['Total', sumCount, bwExportDecimal(sumHrs), totalAvg]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyBoldToRows(ws, [totalRowIdx], headers.length);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  setSheetColumnWidths(ws, [36, 16, 14, 16]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'By type');
  const fileName = bwXlsxFilename('bandwidth_activity_count_time_by_type', dateFrom, dateTo);
  return { blob: workbookToXlsxBlob(wb), fileName };
}

/** Pass rows in the same order as the on-screen table (e.g. panel `sorted`). */
export function exportBandwidthUserCountTimeExcel(
  rows: BandwidthUserCountTimePoint[],
  dateFrom: string,
  dateTo: string
): BandwidthExcelExport {
  const headers: SheetCell[] = ['User', 'Activity count', 'Time (hrs)', 'Avg hrs / activity'];
  const aoa: SheetCell[][] = [headers];
  let sumCount = 0;
  let sumHrs = 0;
  for (const r of rows) {
    const avg = r.totalActivityCount > 0 ? r.timeSpentHours / r.totalActivityCount : null;
    aoa.push([
      r.userName,
      r.totalActivityCount,
      bwExportDecimal(r.timeSpentHours),
      bwExportDecimal(avg),
    ]);
    sumCount += r.totalActivityCount;
    sumHrs += r.timeSpentHours;
  }
  const totalAvg = sumCount > 0 ? roundBwDecimal(sumHrs / sumCount) : '';
  const totalRowIdx = aoa.length;
  aoa.push(['Total', sumCount, bwExportDecimal(sumHrs), totalAvg]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyBoldToRows(ws, [totalRowIdx], headers.length);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  setSheetColumnWidths(ws, [28, 16, 14, 16]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'By user');
  const fileName = bwXlsxFilename('bandwidth_activity_count_time_by_user', dateFrom, dateTo);
  return { blob: workbookToXlsxBlob(wb), fileName };
}

function allocationExcelRow(metrics: BandwidthAllocationMetrics): SheetCell[] {
  return [
    bwExportDecimal(metrics.totalHours),
    bwExportDecimal(metrics.billableHours),
    bwExportDecimal(metrics.nonBillableHours),
    bwExportDecimal(metrics.availableHours),
    allocationExportPct(metrics.bandwidthUtilizedPct),
    allocationExportPct(metrics.bandwidthAvailablePct),
  ];
}

/** Team + resource allocation rows (By Team and Resource View). */
export function exportBandwidthTeamResourceAllocationExcel(
  rows: BandwidthTeamResourceAllocationRow[],
  dateFrom: string,
  dateTo: string
): BandwidthExcelExport | null {
  if (rows.length === 0) return null;
  const headers: SheetCell[] = [
    'Team / Resource',
    'Type',
    'Total hours',
    'Billable hours',
    'Non billable hours',
    'Available hours',
    'Bandwidth utilized %',
    'Bandwidth available %',
  ];
  const aoa: SheetCell[][] = [headers];
  const boldRowIndices: number[] = [];

  for (const team of rows) {
    boldRowIndices.push(aoa.length);
    aoa.push([team.label, 'Team', ...allocationExcelRow(team)]);
    for (const resource of team.resources ?? []) {
      aoa.push([resource.label, 'Resource', ...allocationExcelRow(resource)]);
    }
  }

  const footer = computeAllocationFooter(
    rows,
    rows.reduce((sum, team) => sum + team.totalHours, 0)
  );
  boldRowIndices.push(aoa.length);
  aoa.push(['Total', '', ...allocationExcelRow(footer)]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyBoldToRows(ws, boldRowIndices, headers.length);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  setSheetColumnWidths(ws, [28, 12, 14, 14, 16, 14, 18, 18]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Team resource');
  const fileName = bwXlsxFilename('bandwidth_team_resource_allocation', dateFrom, dateTo);
  return { blob: workbookToXlsxBlob(wb), fileName };
}

function activityAllocationExcelRow(row: BandwidthActivityAllocationRow): SheetCell[] {
  return [
    bwExportDecimal(row.totalHours),
    bwExportDecimal(row.billableHours),
    bwExportDecimal(row.nonBillableHours),
    allocationExportPct(row.billablePct),
    allocationExportPct(row.nonBillablePct),
  ];
}

/** Activity allocation rows (By Activity View). */
export function exportBandwidthActivityAllocationExcel(
  rows: BandwidthActivityAllocationRow[],
  dateFrom: string,
  dateTo: string
): BandwidthExcelExport | null {
  if (rows.length === 0) return null;
  const headers: SheetCell[] = [
    'Activity',
    'Total hours',
    'Billable hours',
    'Non billable hours',
    'Billable %',
    'Non billable %',
  ];
  const aoa: SheetCell[][] = [headers];
  const boldRowIndices: number[] = [];

  for (const row of rows) {
    aoa.push([row.activityTypeLabel, ...activityAllocationExcelRow(row)]);
  }

  const footer = computeActivityAllocationFooter(rows);
  boldRowIndices.push(aoa.length);
  aoa.push(['Total', ...activityAllocationExcelRow({ activityType: '', activityTypeLabel: '', ...footer })]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyBoldToRows(ws, boldRowIndices, headers.length);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  setSheetColumnWidths(ws, [36, 14, 14, 16, 12, 12]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'By activity');
  const fileName = bwXlsxFilename('bandwidth_activity_allocation', dateFrom, dateTo);
  return { blob: workbookToXlsxBlob(wb), fileName };
}
